import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { UNITS, getLesson, lessonIndex } from "../lib/homeKeyCurriculum";
import { LESSON_MOMENTS } from "../lib/homeKeyLessons";
import {
  getUserId,
  subscribeProgress,
  saveProgress,
  emptyProgress,
} from "../lib/homeKeyProgress";
import {
  PATCHES,
  startDrone,
  playPhrase,
  playPhraseLoop,
  resumeAudio,
} from "../lib/homeKeyAudio";
import {
  STYLE_LENSES,
  GROOVE_PATCHES,
  startGroove,
  playTrackMix,
} from "../lib/homeKeyGroove";
import "../styles/homekey.css";

/* ============================================================
   /home-key/studio — the track so far. One row per layer: the
   drone bed, then every saved keeper (playable solo), then
   dimmed placeholders for the layers later units will add.
   The Chords row comes alive once the chordLoop keeper exists.

   Unit 6 adds the B side: the minorLoop keeper gets its own
   row (loops twice, like Chords), and once bSection exists a
   "Play the B side" button layers the minor loop under the
   B-section melody — the track's shadow half.

   Units 7–8 turn this page into the mix desk (Studio v2):
   - Drums row: live once the beat keeper exists (the-grid's
     pulse stands in before then) — ▶ loops the pattern via
     startGroove at the saved style lens's bpm/swing.
   - Bass row: live once bassline exists — ▶ plays the phrase
     with the bassDrop patch, exactly as recorded.
   - Style row: once trackStyle exists it shows the lens and
     grows swap chips — re-saving trackStyle from here is the
     learner remixing their own track.
   - "Play my track" renders every available layer through
     playTrackMix under the saved lens (dreamPop when unset);
     "Play the B side" keeps its original manual layering.
   - Once arrangement exists, "Play the whole song" walks its
     sections (A / B / quiet) one 4-bar cycle each — the
     course's final artifact, so the desk glows for it.
   ============================================================ */

const unitTitle = (unitId) => {
  const unit = UNITS.find((u) => u.id === unitId);
  return unit ? unit.title : unitId;
};

// Display name a lesson's keeper-saving moment (`try` or `build`) gave a
// keeper (e.g. "the homecoming", "your chord loop").
const keeperName = (keeperId, ownerLessonId) => {
  const lessonIds = ownerLessonId
    ? [ownerLessonId]
    : Object.keys(LESSON_MOMENTS);
  for (const lid of lessonIds) {
    const moment = (LESSON_MOMENTS[lid] || []).find(
      (m) => m.keeperId === keeperId
    );
    if (moment) return moment.keeperName || keeperId;
  }
  return keeperId;
};

// Keepers with a dedicated row (or an info chip) below — the generic keeper
// list must not render them a second time.
const DEDICATED_KEEPERS = [
  "chordLoop",
  "minorLoop",
  "pulse",
  "beat",
  "bassline",
  "layerMap",
  "energyArc",
  "trackStyle",
  "arrangement",
];

// What one arrangement section plays, mirroring ArrangeBuild exactly so the
// whole song here sounds like the capstone's preview: B swaps in the shadow
// half (the bass sits out — it was written against the A chords), quiet
// strips back to the chords alone.
const SONG_MIXES = {
  A: { section: "A", layers: {} },
  B: { section: "B", layers: { bass: false } },
  quiet: {
    section: "A",
    layers: { drums: false, bass: false, melody: false, drone: false },
  },
};

// Layers later units add. "Chords" comes alive once the chordLoop keeper
// exists (Unit 4), "B side" with minorLoop (Unit 6), "Drums" and "Bass" with
// Unit 7's keepers, "Sound & style" with Unit 8's trackStyle.
const FUTURE_LAYERS = [
  { id: "chords", label: "Chords", unitId: "chords" },
  { id: "bside", label: "B side", unitId: "minor-side" },
  { id: "bass", label: "Bass", unitId: "groove" },
  { id: "drums", label: "Drums", unitId: "groove" },
  { id: "dressing", label: "Sound & style", unitId: "layers" },
];

const HomeKeyStudio = () => {
  const [progress, setProgress] = useState(emptyProgress);
  // "drone" | "track" | "song" | "drums" | "bass" | keeperId | null
  const [playing, setPlaying] = useState(null);

  const handlesRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    const unsub = subscribeProgress(getUserId(), setProgress);
    return unsub;
  }, []);

  const stopAll = useCallback(() => {
    handlesRef.current.forEach((h) => h && h.stop && h.stop());
    handlesRef.current = [];
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setPlaying(null);
  }, []);

  // Silence everything when leaving the studio.
  useEffect(() => stopAll, [stopAll]);

  const playKeeper = (keeperId, phrase) => {
    resumeAudio();
    stopAll();
    const handle = playPhrase(phrase, PATCHES.softKeys);
    handlesRef.current.push(handle);
    setPlaying(keeperId);
    timerRef.current = setTimeout(
      () => setPlaying(null),
      (handle.durMs || 2000) + 400
    );
  };

  const toggleDrone = () => {
    if (playing === "drone") {
      stopAll();
      return;
    }
    resumeAudio();
    stopAll();
    handlesRef.current.push(startDrone(43));
    setPlaying("drone");
  };

  const chordLoop = progress.keepers.chordLoop;
  const trackMelody = progress.keepers.trackMelody;
  const minorLoop = progress.keepers.minorLoop;
  const bSection = progress.keepers.bSection;
  const pulse = progress.keepers.pulse;
  const beat = progress.keepers.beat;
  const bassline = progress.keepers.bassline;
  const layerMap = progress.keepers.layerMap;
  const energyArc = progress.keepers.energyArc;
  const trackStyle = progress.keepers.trackStyle;
  const arrangement = progress.keepers.arrangement;

  // The melody the track features: the Unit 5 rewrite once it exists,
  // otherwise the Unit 1 home call.
  const melodyKeeperId = trackMelody ? "trackMelody" : "homeCall";

  // The track's style lens: the trackStyle keeper when saved (and known),
  // dreamPop otherwise. Every mixed playback on this page wears it.
  const lensId =
    trackStyle && STYLE_LENSES[trackStyle.lensId]
      ? trackStyle.lensId
      : "dreamPop";
  const lens = STYLE_LENSES[lensId];

  // The drums the track owns: the full beat once Unit 7's second lesson is
  // done, the kick-only pulse before that.
  const drumKeeper =
    (beat && beat.pattern && beat) || (pulse && pulse.pattern && pulse) || null;
  const drumKeeperId = beat && beat.pattern ? "beat" : "pulse";

  const toggleChords = () => {
    if (playing === "chords") {
      stopAll();
      return;
    }
    if (!chordLoop || !chordLoop.phrase) return;
    resumeAudio();
    stopAll();
    const handle = playPhraseLoop(chordLoop.phrase, { times: 2 });
    handlesRef.current.push(handle);
    setPlaying("chords");
    timerRef.current = setTimeout(
      () => setPlaying(null),
      (handle.durMs || 4000) + 400
    );
  };

  const toggleMinorLoop = () => {
    if (playing === "minorLoop") {
      stopAll();
      return;
    }
    if (!minorLoop || !minorLoop.phrase) return;
    resumeAudio();
    stopAll();
    const handle = playPhraseLoop(minorLoop.phrase, { times: 2 });
    handlesRef.current.push(handle);
    setPlaying("minorLoop");
    timerRef.current = setTimeout(
      () => setPlaying(null),
      (handle.durMs || 4000) + 400
    );
  };

  // Drums solo: loop the saved pattern at the saved lens's tempo and swing.
  // dreamPop wears no drums of its own (patternKey null) — the keeper's
  // pattern still plays, just at dreamPop's unhurried 84.
  const toggleDrums = () => {
    if (playing === "drums") {
      stopAll();
      return;
    }
    if (!drumKeeper) return;
    resumeAudio();
    stopAll();
    handlesRef.current.push(
      startGroove({
        pattern: drumKeeper.pattern,
        bpm: lens.bpm,
        swing: lens.swing,
        bars: 1,
      })
    );
    setPlaying("drums");
  };

  // Bass solo: the phrase exactly as recorded (already at the octave it was
  // played back at record time — no extra shift), in the bassDrop voice.
  const playBassline = () => {
    if (playing === "bass") {
      stopAll();
      return;
    }
    if (!bassline || !bassline.phrase) return;
    resumeAudio();
    stopAll();
    const handle = playPhrase(bassline.phrase, GROOVE_PATCHES.bassDrop);
    handlesRef.current.push(handle);
    setPlaying("bass");
    timerRef.current = setTimeout(
      () => setPlaying(null),
      (handle.durMs || 2000) + 400
    );
  };

  // Re-dress the track: update the trackStyle keeper through the normal
  // progress save path. The Studio is the learner's mix desk — swapping the
  // lens here is the same act as picking it in the dressing lesson.
  const swapLens = (nextLensId) => {
    if (!trackStyle || !STYLE_LENSES[nextLensId] || nextLensId === lensId) {
      return;
    }
    const next = {
      ...progress,
      keepers: {
        ...progress.keepers,
        trackStyle: { ...trackStyle, lensId: nextLensId, savedAt: Date.now() },
      },
    };
    // saveProgress returns the stamped state; set it so local-only mode (no
    // Firestore echo) updates immediately too.
    setProgress(saveProgress(getUserId(), next));
  };

  // The B side: the minor loop humming under the B-section melody, exactly
  // the bed the melody was recorded over (minorLoop x4). If the loop is
  // missing (kept-anyway paths), an E drone stands in — same fallback the
  // lesson's recorder uses.
  const playBSide = () => {
    if (!bSection || !bSection.phrase) return;
    resumeAudio();
    stopAll();
    setPlaying("bSide");
    let endMs = 0;
    if (minorLoop && minorLoop.phrase) {
      const handle = playPhraseLoop(minorLoop.phrase, { times: 4 });
      handlesRef.current.push(handle);
      endMs = Math.max(endMs, handle.durMs || 0);
    } else {
      handlesRef.current.push(startDrone(40));
    }
    const handle = playPhrase(bSection.phrase, PATCHES.softKeys);
    handlesRef.current.push(handle);
    endMs = Math.max(endMs, handle.durMs || 0);
    if (endMs) {
      timerRef.current = setTimeout(stopAll, endMs + 1600);
    }
  };

  // The A side, full band: every available layer through playTrackMix under
  // the saved lens. Four cycles (the old four passes of the chord loop),
  // then a ring-out.
  const playTrack = () => {
    resumeAudio();
    stopAll();
    setPlaying("track");
    const handle = playTrackMix({
      keepers: progress.keepers,
      lensId,
      layers: {},
    });
    handlesRef.current.push(handle);
    const cycleMs = handle.cycleMs || 4000;
    timerRef.current = setTimeout(stopAll, cycleMs * 4 + 1600);
  };

  // The whole song: the arrangement's sections in order, one 4-bar cycle
  // each, each section its own playTrackMix. The course's final artifact.
  const playWholeSong = () => {
    if (playing === "song") {
      stopAll();
      return;
    }
    const sections = (arrangement && arrangement.sections) || [];
    if (!sections.length) return;
    resumeAudio();
    stopAll();
    setPlaying("song");
    const step = (i) => {
      // Hand off cleanly: silence the previous section's mix first.
      handlesRef.current.forEach((h) => h && h.stop && h.stop());
      handlesRef.current = [];
      if (i >= sections.length) {
        stopAll();
        return;
      }
      const mix = SONG_MIXES[sections[i]] || SONG_MIXES.A;
      const handle = playTrackMix({
        keepers: progress.keepers,
        lensId,
        ...mix,
      });
      handlesRef.current.push(handle);
      timerRef.current = setTimeout(
        () => step(i + 1),
        Math.max(400, handle.cycleMs || 4000)
      );
    };
    step(0);
  };

  const keeperRows = Object.entries(progress.keepers)
    // Keepers with dedicated rows / chips below don't render twice here.
    .filter(([id]) => !DEDICATED_KEEPERS.includes(id))
    .map(([id, keeper]) => ({ id, ...keeper }))
    .sort((a, b) => lessonIndex(a.lessonId) - lessonIndex(b.lessonId));

  const hasSong = Boolean(
    arrangement && arrangement.sections && arrangement.sections.length
  );
  // "track" lights the whole desk; the whole song lights it brighter.
  const mixGlow = playing === "track" || playing === "song";

  return (
    <div className="hk-root">
      <div className="hk-studio">
        <header className="hk-studio-header">
          <Link to="/home-key" className="hk-back-link">
            ← back to the map
          </Link>
          <h1 className="hk-studio-title">The Studio</h1>
          <p className="hk-studio-subtitle">
            Your track so far — every keeper you save adds a layer.
          </p>
          <div className="hk-studio-actions">
            <button
              type="button"
              className="hk-btn hk-btn--primary"
              onClick={playTrack}
            >
              ▶ Play my track
            </button>
            {hasSong && (
              <button
                type="button"
                className={`hk-btn hk-btn--primary hk-btn--song${
                  playing === "song" ? " hk-btn--song-on" : ""
                }`}
                onClick={playWholeSong}
              >
                {playing === "song"
                  ? "■ Stop the song"
                  : "✦ Play the whole song"}
              </button>
            )}
            {bSection && bSection.phrase && (
              <button
                type="button"
                className="hk-btn hk-btn--ghost hk-btn--bside"
                onClick={playBSide}
              >
                ▶ Play the B side
              </button>
            )}
            <button
              type="button"
              className="hk-btn hk-btn--ghost"
              onClick={stopAll}
              disabled={!playing}
            >
              ■ Stop
            </button>
          </div>
        </header>

        <div className="hk-track">
          <div
            className={`hk-track-row hk-track-row--drone${
              playing === "drone" || mixGlow ? " hk-track-row--playing" : ""
            }`}
          >
            <div className="hk-keeper-card">
              <span className="hk-track-name">Drone bed</span>
              <span className="hk-track-sub">
                a low G, humming underneath everything
              </span>
            </div>
            <button
              type="button"
              className="hk-btn hk-btn--ghost"
              onClick={toggleDrone}
            >
              {playing === "drone" ? "■ stop" : "▶ play"}
            </button>
          </div>

          {keeperRows.map((keeper) => {
            const lesson = getLesson(keeper.lessonId);
            return (
              <div
                key={keeper.id}
                className={`hk-track-row${
                  playing === keeper.id ||
                  mixGlow ||
                  (playing === "bSide" && keeper.id === "bSection")
                    ? " hk-track-row--playing"
                    : ""
                }`}
              >
                <div className="hk-keeper-card">
                  <span className="hk-track-name">
                    ✦ {keeperName(keeper.id, keeper.lessonId)}
                  </span>
                  <span className="hk-track-sub">
                    {lesson ? `from “${lesson.title}”` : "a saved phrase"}
                    {keeper.id === melodyKeeperId &&
                      " · loops in your track"}
                  </span>
                </div>
                <button
                  type="button"
                  className="hk-btn hk-btn--ghost"
                  onClick={() => playKeeper(keeper.id, keeper.phrase)}
                >
                  {playing === keeper.id ? "playing…" : "▶ play"}
                </button>
              </div>
            );
          })}

          {keeperRows.length === 0 && (
            <div className="hk-track-empty">
              <p>
                No keepers yet. Every lesson ends with a little music you make
                and keep — it lands here.
              </p>
              <Link to="/home-key" className="hk-btn hk-btn--primary">
                Start the first lesson
              </Link>
            </div>
          )}

          {FUTURE_LAYERS.map((layer) => {
            if (layer.id === "chords" && chordLoop && chordLoop.phrase) {
              return (
                <div
                  key={layer.id}
                  className={`hk-track-row${
                    playing === "chords" || mixGlow
                      ? " hk-track-row--playing"
                      : ""
                  }`}
                >
                  <div className="hk-keeper-card">
                    <span className="hk-track-name">
                      ✦ {keeperName("chordLoop", chordLoop.lessonId)}
                    </span>
                    <span className="hk-track-sub">
                      {chordLoop.degrees && chordLoop.degrees.length
                        ? `the ${chordLoop.degrees.join(" – ")} loop`
                        : "your chords"}
                      {" · loops in your track"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="hk-btn hk-btn--ghost"
                    onClick={toggleChords}
                  >
                    {playing === "chords" ? "■ stop" : "▶ play"}
                  </button>
                </div>
              );
            }
            if (layer.id === "bside" && minorLoop && minorLoop.phrase) {
              return (
                <div
                  key={layer.id}
                  className={`hk-track-row hk-track-row--bside${
                    playing === "minorLoop" ||
                    playing === "bSide" ||
                    playing === "song"
                      ? " hk-track-row--playing"
                      : ""
                  }`}
                >
                  <div className="hk-keeper-card">
                    <span className="hk-track-name">
                      ✧ {keeperName("minorLoop", minorLoop.lessonId)}
                    </span>
                    <span className="hk-track-sub">
                      {minorLoop.degrees && minorLoop.degrees.length
                        ? `the ${minorLoop.degrees
                            .map((d) => (d === "5+" ? "5♯" : d))
                            .join(" – ")} loop, in shadow`
                        : "your minor chords"}
                      {bSection && bSection.phrase
                        ? " · the B side's bed"
                        : " · waiting for its melody"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="hk-btn hk-btn--ghost"
                    onClick={toggleMinorLoop}
                  >
                    {playing === "minorLoop" ? "■ stop" : "▶ play"}
                  </button>
                </div>
              );
            }
            if (layer.id === "drums" && drumKeeper) {
              return (
                <div
                  key={layer.id}
                  className={`hk-track-row${
                    playing === "drums" || mixGlow
                      ? " hk-track-row--playing"
                      : ""
                  }`}
                >
                  <div className="hk-keeper-card">
                    <span className="hk-track-name">
                      ✦ {keeperName(drumKeeperId, drumKeeper.lessonId)}
                    </span>
                    <span className="hk-track-sub">
                      {drumKeeperId === "beat"
                        ? "kick, snare, hat"
                        : "just the pulse, for now"}
                      {` · ${lens.bpm} bpm, ${
                        lens.swing > 0.5 ? "swung lazy" : "straight"
                      }`}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="hk-btn hk-btn--ghost"
                    onClick={toggleDrums}
                  >
                    {playing === "drums" ? "■ stop" : "▶ play"}
                  </button>
                </div>
              );
            }
            if (layer.id === "bass" && bassline && bassline.phrase) {
              return (
                <div
                  key={layer.id}
                  className={`hk-track-row${
                    playing === "bass" || mixGlow
                      ? " hk-track-row--playing"
                      : ""
                  }`}
                >
                  <div className="hk-keeper-card">
                    <span className="hk-track-name">
                      ✦ {keeperName("bassline", bassline.lessonId)}
                    </span>
                    <span className="hk-track-sub">
                      roots in the pocket · locks to your kick in the mix
                    </span>
                  </div>
                  <button
                    type="button"
                    className="hk-btn hk-btn--ghost"
                    onClick={playBassline}
                  >
                    {playing === "bass" ? "playing…" : "▶ play"}
                  </button>
                </div>
              );
            }
            if (layer.id === "dressing" && trackStyle) {
              return (
                <div
                  key={layer.id}
                  className={`hk-track-row hk-track-row--style${
                    mixGlow ? " hk-track-row--playing" : ""
                  }`}
                >
                  <div className="hk-keeper-card">
                    <span className="hk-track-name">
                      ✦ {keeperName("trackStyle", trackStyle.lessonId)}
                    </span>
                    <span className="hk-track-sub">{lens.blurb}</span>
                  </div>
                  <div
                    className="hk-lens-swap"
                    role="group"
                    aria-label="swap the track's style"
                  >
                    {Object.values(STYLE_LENSES).map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        className={`hk-lens-chip${
                          l.id === lensId ? " hk-lens-chip--on" : ""
                        }`}
                        onClick={() => swapLens(l.id)}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            }
            return (
              <div key={layer.id} className="hk-track-row hk-track-row--future">
                <div className="hk-keeper-card">
                  <span className="hk-track-name">{layer.label}</span>
                  <span className="hk-track-sub">
                    arrives in {unitTitle(layer.unitId)}
                  </span>
                </div>
                <span className="hk-track-soon">later</span>
              </div>
            );
          })}

          {hasSong && (
            <div
              className={`hk-track-row hk-track-row--song${
                playing === "song" ? " hk-track-row--playing" : ""
              }`}
            >
              <div className="hk-keeper-card">
                <span className="hk-track-name">
                  ✦ {keeperName("arrangement", arrangement.lessonId)}
                </span>
                <span className="hk-track-sub">
                  {arrangement.sections
                    .map((s) => (s === "quiet" ? "·" : s))
                    .join(" — ")}
                  {" · the whole song, in order"}
                </span>
              </div>
              <button
                type="button"
                className="hk-btn hk-btn--ghost"
                onClick={playWholeSong}
              >
                {playing === "song" ? "■ stop" : "▶ play"}
              </button>
            </div>
          )}
        </div>

        {(layerMap || energyArc) && (
          <div className="hk-studio-chips">
            {layerMap && (
              <span className="hk-info-chip">
                ✧ {keeperName("layerMap", layerMap.lessonId)} — shaping the mix
              </span>
            )}
            {energyArc && (
              <span className="hk-info-chip">
                ✧ {keeperName("energyArc", energyArc.lessonId)} — shaping the
                energy
              </span>
            )}
          </div>
        )}

        <footer className="hk-studio-footer">
          <p>
            {hasSong
              ? "This is your song. Every layer in it is something you made — and understand."
              : "The track grows as the course does — chords under your melody, a bassline in the pocket, drums, a style of its own."}
          </p>
        </footer>
      </div>
    </div>
  );
};

export default HomeKeyStudio;
